import { fetchQuery, graphql } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics/utils/fire-operational-event';

import type { LookupComponents } from '../../common/types/lookup';
import { ErrorWithStatusCode } from '../../common/utils/errors/error-with-status-code';
import type {
	fetchComponentsQuery,
	fetchComponentsQuery$data,
} from './__generated__/fetchComponentsQuery.graphql';

export const fetchComponents = async (
	environment: IEnvironment,
	cloudId: string,
	projectIdOrKey: string,
	componentIds: string[],
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<LookupComponents> => {
	try {
		const apiStartTime = performance.now();

		const response: fetchComponentsQuery$data | undefined = await fetchQuery<fetchComponentsQuery>(
			environment,
			graphql`
				query fetchComponentsQuery(
					$cloudId: ID!
					$idOrKey: String!
					$componentIds: [ID!]!
					$first: Int!
				) {
					jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $idOrKey) {
						components(first: $first, filterById: { ids: $componentIds, operation: ALLOW }) {
							edges {
								node {
									componentId
									name
								}
							}
						}
					}
				}
			`,
			{ cloudId, idOrKey: projectIdOrKey, componentIds, first: componentIds.length },
			{
				networkCacheConfig: { force: false },
				fetchPolicy: 'store-or-network',
			},
		).toPromise();

		const connection = response?.jira_projectByIdOrKey?.components;

		if (!connection) {
			throw new ErrorWithStatusCode(
				'[fetch-components service] [fetchComponentsQuery]: Property jira_projectByIdOrKey.components does not exist.',
				{
					cause: 'FETCH_COMPONENTS_MISSING_COMPONENTS_PROPERTY',
				},
				'FETCH_COMPONENTS_MISSING_COMPONENTS_PROPERTY',
			);
		}

		const resolved: LookupComponents = [];

		for (const edge of connection.edges ?? []) {
			const node = edge?.node;
			if (node?.componentId) {
				resolved.push({ id: node.componentId, name: node.name ?? '' });
			}
		}

		const apiLatencyMs = Math.floor(performance.now() - apiStartTime);

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModificationsFetchComponents',
			attributes: {
				componentsAPILatency: apiLatencyMs,
			},
		});

		return resolved;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		if (!err.cause) {
			err.cause = 'FETCH_COMPONENTS_UNKNOWN_ERROR';
		}

		throw err;
	}
};
